/**
 * Additional-asset list — extra domains and IPs beyond the primary.
 *
 * Each entry is either:
 *  - a domain card that runs its own discovery and reuses
 *    <SubdomainGrid /> for cherry-picking, OR
 *  - an IP card with a single input + light validation feedback.
 *
 * The "+ Add domain" / "+ Add IP" buttons are footers under the
 * list. New entries get a `crypto.randomUUID()` id that lives only
 * in browser state — never reaches the server. The server
 * reclassifies each asset's kind on submission, so even if the
 * buyer adds an IP via the "domain" button it's repriced
 * correctly (see classify_asset in pricing.py).
 *
 * Pricing semantics surfaced to the buyer here:
 *  - Each additional asset (domain or IP) is $175.
 *  - For domains, the 5-subdomain free pool is SHARED with the
 *    primary card — the SubdomainGrid uses the global selection
 *    count from selectors.totalSelectedSubdomains().
 */

import { useCallback } from "react";

import { EXTRA_ASSET_PRICE_LABEL } from "../constants";
import type {
  AdditionalAsset,
  ReconConfiguratorAction,
  ReconConfiguratorState,
} from "../state";
import { totalSelectedSubdomains } from "../selectors";
import { SubdomainGrid } from "./SubdomainGrid";

import "./additional-asset-list.css";

interface Props {
  state: ReconConfiguratorState;
  dispatch: React.Dispatch<ReconConfiguratorAction>;
  /** Triggered when the buyer asks to discover subdomains for a
   *  domain-kind additional asset. The parent owns the AbortController
   *  + request-id lifecycle (mirrors runPrimaryDiscovery). */
  onDiscoverDomain: (assetId: string, value: string) => void | Promise<void>;
}

export function AdditionalAssetList({
  state,
  dispatch,
  onDiscoverDomain,
}: Props) {
  // Only meaningful once the primary scope has been declared.
  // Before then, the section is rendered but the Add buttons are
  // the primary visual — there's nothing else to look at.
  const primaryDeclared = state.primaryDomain.trim().length > 0;

  const globalSubCount = totalSelectedSubdomains(state);

  const addDomain = useCallback(() => {
    dispatch({
      type: "add_additional_domain",
      id: crypto.randomUUID(),
      value: "",
    });
  }, [dispatch]);

  const addIp = useCallback(() => {
    dispatch({
      type: "add_additional_ip",
      id: crypto.randomUUID(),
      value: "",
    });
  }, [dispatch]);

  return (
    <section
      className="additional-asset-list"
      aria-labelledby="additional-asset-list-heading"
    >
      <header className="additional-asset-list-header">
        <div className="additional-asset-list-eyebrow">
          STEP 2 · MORE TO COVER?
        </div>
        <h2 id="additional-asset-list-heading">
          Other public-facing assets{" "}
          <span className="additional-asset-list-price">
            {EXTRA_ASSET_PRICE_LABEL} each
          </span>
        </h2>
        <p className="additional-asset-list-body">
          Secondary brand domains, marketing sites on a different TLD,
          or public IPs for self-hosted services. Each additional
          domain or IP gets the same end-to-end investigation as your
          primary apex and lands in the same report. Subdomains on
          additional domains draw from the same 5-included pool;
          {" "}<strong>{EXTRA_ASSET_PRICE_LABEL}</strong> per
          additional asset.
        </p>
      </header>

      {state.additionalAssets.length > 0 && (
        <ul className="additional-asset-list-items" role="list">
          {state.additionalAssets.map((asset) => (
            <li key={asset.id} className="additional-asset-list-item">
              <AdditionalAssetCard
                asset={asset}
                dispatch={dispatch}
                globalSubCount={globalSubCount}
                onDiscoverDomain={onDiscoverDomain}
              />
            </li>
          ))}
        </ul>
      )}

      <div className="additional-asset-list-add-row">
        <button
          type="button"
          className="additional-asset-list-add-btn"
          onClick={addDomain}
          disabled={!primaryDeclared}
        >
          + Add domain
        </button>
        <button
          type="button"
          className="additional-asset-list-add-btn"
          onClick={addIp}
          disabled={!primaryDeclared}
        >
          + Add IP
        </button>
        {!primaryDeclared && (
          <span className="additional-asset-list-add-hint">
            Set your primary domain above first.
          </span>
        )}
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------- //
// One additional-asset card                                                   //
// --------------------------------------------------------------------------- //

interface CardProps {
  asset: AdditionalAsset;
  dispatch: React.Dispatch<ReconConfiguratorAction>;
  globalSubCount: number;
  onDiscoverDomain: (assetId: string, value: string) => void | Promise<void>;
}

function AdditionalAssetCard({
  asset,
  dispatch,
  globalSubCount,
  onDiscoverDomain,
}: CardProps) {
  const trimmed = asset.value.trim();
  const isLoading = asset.discovery.kind === "loading";
  const isError = asset.discovery.kind === "error";
  const isReady = asset.discovery.kind === "ok";

  const handleRemove = useCallback(() => {
    dispatch({ type: "remove_additional_asset", id: asset.id });
  }, [dispatch, asset.id]);

  const handleScan = useCallback(() => {
    if (!trimmed || isLoading) return;
    void onDiscoverDomain(asset.id, asset.value);
  }, [trimmed, isLoading, onDiscoverDomain, asset.id, asset.value]);

  // For IP cards: best-effort syntactic validation hint. The server
  // reclassifies + validates definitively at price-preview time, so
  // this is just an early visual cue.
  const ipHint =
    asset.kind === "ip" && trimmed
      ? looksLikeIp(trimmed)
        ? null
        : "Doesn't look like an IPv4 or IPv6 address. We'll re-check on the next price update."
      : null;

  const selectedSet = new Set(asset.selectedSubs.map((s) => s.toLowerCase()));

  return (
    <article
      className={`additional-asset-card additional-asset-card-${asset.kind}`}
      aria-label={
        asset.kind === "domain"
          ? "Additional domain"
          : "Additional IP address"
      }
    >
      <header className="additional-asset-card-header">
        <div className="additional-asset-card-kind">
          {asset.kind === "domain" ? "Domain" : "IP address"}
          <span className="additional-asset-card-price">
            {EXTRA_ASSET_PRICE_LABEL}
          </span>
        </div>
        <button
          type="button"
          className="additional-asset-card-remove"
          onClick={handleRemove}
          aria-label="Remove this asset"
          title="Remove"
        >
          ×
        </button>
      </header>

      <div className="additional-asset-card-row">
        <input
          type="text"
          inputMode={asset.kind === "ip" ? "numeric" : "url"}
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          value={asset.value}
          onChange={(e) =>
            dispatch({
              type: "set_additional_value",
              id: asset.id,
              value: e.target.value,
            })
          }
          placeholder={
            asset.kind === "domain" ? "marketing.acme.com" : "203.0.113.42"
          }
        />
        {asset.kind === "domain" && (
          <button
            type="button"
            className="additional-asset-card-scan"
            onClick={handleScan}
            disabled={!trimmed || isLoading}
          >
            {isLoading ? "Scanning…" : isReady ? "Rescan" : "Scan"}
          </button>
        )}
      </div>

      {ipHint && (
        <p className="additional-asset-card-status additional-asset-card-status-warn">
          {ipHint}
        </p>
      )}

      {isError && (
        <p
          className="additional-asset-card-status additional-asset-card-status-error"
          role="alert"
        >
          {asset.discovery.kind === "error" ? asset.discovery.message : ""}
        </p>
      )}

      {asset.kind === "domain" && isReady && (
        <SubdomainGrid
          assets={asset.discovery.kind === "ok" ? asset.discovery.data : []}
          apex={asset.value}
          selectedSet={selectedSet}
          onToggle={(hostname) =>
            dispatch({
              type: "toggle_additional_sub",
              id: asset.id,
              hostname,
            })
          }
          onClearAll={() => {
            // No bulk "clear all" action for additional assets yet —
            // toggle each one off via the same action.
            for (const sub of asset.selectedSubs) {
              dispatch({
                type: "toggle_additional_sub",
                id: asset.id,
                hostname: sub,
              });
            }
          }}
          globalSelectedCount={globalSubCount}
          variant="additional"
          idPrefix={`additional-${asset.id}`}
        />
      )}
    </article>
  );
}

// --------------------------------------------------------------------------- //
// IP shape sniffing — visual hint only; not authoritative                     //
// --------------------------------------------------------------------------- //

function looksLikeIp(value: string): boolean {
  // IPv4: four dotted octets in 0–255.
  const v4 = /^(?:\d{1,3}\.){3}\d{1,3}$/;
  if (v4.test(value)) {
    const octets = value.split(".").map((s) => Number.parseInt(s, 10));
    return octets.every((o) => Number.isFinite(o) && o >= 0 && o <= 255);
  }
  // IPv6: at least one colon, all chars in [0-9a-fA-F:]. Loose
  // sniff — full RFC 4291 parsing is the server's job.
  return value.includes(":") && /^[0-9a-fA-F:]+$/.test(value);
}
